import {
  inject,
  InjectionToken,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
  type EnvironmentProviders,
} from '@angular/core';

import { SessionStorageService } from '@cosmos/data-access-storage';
import { injectWindow } from '@cosmos/util-common/di';
import { PRESENTATION_CUSTOMER_PREVIEW_OPENER_QUERY_PARAM } from '@esp/presentations/page-modules/types-common';

/**
 * Whether visit tracking should be skipped for this tab, e.g. because the app
 * was opened by the distributor "Customer View" CTA — such a visit belongs to
 * the distributor previewing their own presentation, not to the customer.
 *
 * Only provided by `provideVisitTrackerSkipDetection()`, so inject it
 * optionally and treat a missing value as "track the visit".
 */
export const SKIP_VISIT_TRACKER = new InjectionToken<boolean>(
  ngDevMode ? 'SKIP_VISIT_TRACKER' : ''
);

/** Query params whose presence means this document should not be tracked. */
const SKIP_TRACKER_QUERY_PARAMS = [
  PRESENTATION_CUSTOMER_PREVIEW_OPENER_QUERY_PARAM,
];

const SKIP_TRACKER_STORAGE_KEY = 'skipVisitTracker';

/**
 * Document load types for which a previously stored flag still belongs to this
 * tab: a reload (F5) or a back/forward navigation of the same document. A tab
 * created via `window.open` reports `navigate` instead — which matters, since
 * the browser copies the opener's session storage into such a tab, and an
 * inherited flag must not silence tracking for a presentation opened from it.
 *
 * "Duplicate tab" restores a history entry, so it reports `back_forward` and
 * keeps the flag — intentionally, since the duplicate shows the very same
 * distributor preview.
 */
const SAME_TAB_NAVIGATION_TYPES: ReadonlyArray<NavigationTimingType> = [
  'reload',
  'back_forward',
];

/**
 * Resolves {@link SKIP_VISIT_TRACKER} from the current URL and remembers the
 * decision for this tab, so reloading it keeps the app untracked.
 *
 * Resolution is eager (an environment initializer touches the token), so the
 * skip params are stripped from the address bar before the router runs its
 * initial navigation and its `UrlTree` stays in sync with what the user sees.
 */
export function provideVisitTrackerSkipDetection(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: SKIP_VISIT_TRACKER, useFactory: resolveSkipVisitTracker },
    provideEnvironmentInitializer(() => void inject(SKIP_VISIT_TRACKER)),
  ]);
}

export function injectSkipVisitTracker(): boolean {
  return inject(SKIP_VISIT_TRACKER, { optional: true }) === true;
}

/**
 * This resolver has to be executed eagerly, before the router performs its initial navigation,
 * so that the skip params can be stripped from the URL immediately.
 */
function resolveSkipVisitTracker(): boolean {
  if (ngServerMode) {
    return false;
  }

  const window = injectWindow();
  const sessionStorage = inject(SessionStorageService);
  const url = new URL(window.location.href);
  const hasSkipParam = SKIP_TRACKER_QUERY_PARAMS.some((param) =>
    url.searchParams.has(param)
  );

  if (hasSkipParam) {
    // Remember the decision for this tab, so a reload — which no longer carries
    // the param, see below — keeps skipping the tracker.
    sessionStorage.setItem(SKIP_TRACKER_STORAGE_KEY, true);

    // Drop the params from the address bar, so the behavior is not copied for
    // others when the user shares the URL. Uses the native History API and
    // keeps the router state untouched, since this runs before the router
    // reads the URL for its initial navigation.
    SKIP_TRACKER_QUERY_PARAMS.forEach((param) =>
      url.searchParams.delete(param)
    );
    window.history.replaceState(
      window.history.state,
      '',
      url.pathname + url.search + url.hash
    );

    return true;
  }

  // Session storage is per-tab and survives a reload, but the browser copies it
  // into a tab opened via `window.open`. Such a tab starts with a `navigate`
  // load, so only honor the stored flag on a reload of this very tab, and drop
  // an inherited one — otherwise later reloads would skip tracking as well.
  if (
    isSameDocumentReload(window) &&
    sessionStorage.getItem<boolean>(SKIP_TRACKER_STORAGE_KEY)
  ) {
    return true;
  }

  sessionStorage.removeItem(SKIP_TRACKER_STORAGE_KEY);

  return false;
}

function isSameDocumentReload(window: Window): boolean {
  const [navigation] = (window.performance?.getEntriesByType?.('navigation') ??
    []) as PerformanceNavigationTiming[];
  return !!navigation && SAME_TAB_NAVIGATION_TYPES.includes(navigation.type);
}
